import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DefaultButton, Dropdown, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { Editor, Transforms } from "slate";
import ModalMain from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import ConfirmModal from "../../../../components/Modal/ConfirmModal";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import toastContext from "../../../../context/toast/ToastContext";
import userContext from "../../../../context/users/UserContext";
import AgenticReportingContext from "../../../../context/agentic-reporting/AgenticReportingContext";
import { TOAST_TYPE } from "../../../../constants/Constants";
import { getAiDraftTemplateHtml } from "../../../../service/AiDraftService";
import {
  AI_DRAFT_OUTPUTS,
  activeReportTemplate,
  describeGeneration,
  findingsChatNodes,
  findingsLines,
  friendlyDraftError,
  generationLabels,
  hasFindings,
  hasReport,
  highlightPlaceholders,
  htmlToPlainText,
  isFailed,
  isPending,
  isReady,
  isReportEmpty,
  isUnseen,
  isUntouchedTemplate,
  mentionOf,
  outputSuffix,
  pickInitialGeneration,
  runningModelIds,
  sanitizeDraftHtml,
  sanitizeFindingsHtml,
  waitUntil,
} from "./aiDraftState";
import HistorySummary from "./HistorySummary";
import ProgressSteps, { DRAFT_STEPS } from "./ProgressSteps";
import "./AiDraft.css";

/** Sub-tab of the report panel that holds the addendum (StudyReportState activeTab). */
const ADDENDUM_TAB = 2;

export const NOT_EMPTY_MESSAGE =
  "The report panel is not empty. Clear the report, or open an unedited template, to insert the AI draft.";
export const ADDENDUM_MESSAGE =
  "The addendum is open. Switch to the report tab to insert the AI draft.";
export const NO_CHAT_MESSAGE =
  "Open an Agentic Report tab in the report panel (the LLM chat is shown there), then send the findings.";

/** Last output chosen, per browser; only a convenience, so storage failures are ignored. */
const OUTPUT_STORAGE_KEY = "aiDraftOutput";
const savedOutput = () => {
  try {
    const value = window.localStorage.getItem(OUTPUT_STORAGE_KEY);
    return AI_DRAFT_OUTPUTS.some((o) => o.key === value) ? value : "report";
  } catch {
    return "report";
  }
};
const saveOutput = (value) => {
  try {
    window.localStorage.setItem(OUTPUT_STORAGE_KEY, value);
  } catch {
    // private window or blocked storage: the choice just isn't remembered
  }
};

/** Replaces the chat input's content; the chat's own onChange stores it as the tab's input. */
const fillChatEditor = (editor, nodes) => {
  Editor.withoutNormalizing(editor, () => {
    for (let i = editor.children.length - 1; i >= 0; i--) Transforms.removeNodes(editor, { at: [i] });
    Transforms.insertNodes(editor, nodes, { at: [0] });
  });
  Transforms.select(editor, Editor.end(editor, []));
};

const formatTime = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const formatLatency = (ms) => (ms == null ? "" : `${(ms / 1000).toFixed(1)} s`);

/** Seconds since the generation was requested, for the "Waiting for …" timer. */
const elapsedSeconds = (generation, now) => {
  const started = Date.parse(generation?.created_at || "");
  return Number.isNaN(started) ? null : Math.max(0, Math.floor((now - started) / 1000));
};

/** Generations with their tab label, sanitised HTML, highlighted placeholders and findings. */
const useTabs = (generations) =>
  useMemo(() => {
    const labels = generationLabels(generations);
    return generations.map((g) => {
      const html = hasReport(g) ? sanitizeDraftHtml(g.report_html) : "";
      const findingsHtml = hasFindings(g) ? sanitizeFindingsHtml(g.findings_html) : "";
      return {
        ...g,
        label: labels[g.id] + outputSuffix(g),
        html,
        highlighted: highlightPlaceholders(html),
        findingsHtml,
      };
    });
  }, [generations]);

/**
 * The AI draft panel: a model picker with Generate / Regenerate, and one tab per generation in
 * the order they were requested. Each generation runs one model; several can run at once and a
 * slow model never holds up a fast one. A finished generation in a background tab gets a badge;
 * the panel never switches tabs by itself. Closing a tab only hides it until the study is opened
 * again. Compare shows two finished drafts side by side.
 *
 * "Insert" writes a draft into the active report tab through the report state (the same path the
 * agentic report uses), so it survives the editor re-rendering; nothing is saved or signed. It
 * only inserts into an empty report or an unedited template; otherwise it says the report panel is
 * not empty. "[ ]" placeholders are highlighted here only. The AI-GENERATED DRAFT banner is kept.
 */
const AiDraftPanel = ({ aiDraft, onClose }) => {
  const reportContext = useContext(studyReportContext);
  const agentic = useContext(AgenticReportingContext);
  const { enableLlmReport } = useContext(userContext) || {};
  const { reportMenu, setReportMenu, studyParentId } = useContext(studyviewerContext);
  const { showToastv2 } = useContext(toastContext);
  // findings feed the LLM report chat: offered only to radiologists with the LLM report
  const [output, setOutput] = useState(() => (enableLlmReport ? savedOutput() : "report"));
  // per generation that has both: which one is shown ("report" or "findings")
  const [views, setViews] = useState({});
  const latestAgentic = useRef(agentic);
  latestAgentic.current = agentic;
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [closedIds, setClosedIds] = useState(() => new Set());
  const [compare, setCompare] = useState(false);
  const [comparePair, setComparePair] = useState([null, null]);
  // the insert waits for the report panel to load, so it must read the latest context, not this render's
  const latestReport = useRef(reportContext);
  latestReport.current = reportContext;

  const { generations = [], models } = aiDraft;
  const tabs = useTabs(generations);
  const visibleTabs = tabs.filter((t) => !closedIds.has(t.id));
  const [activeId, setActiveId] = useState(() => pickInitialGeneration(generations)?.id ?? null);
  const current = visibleTabs.find((t) => t.id === activeId) || null;
  // Compare and Insert are about reports; a findings-only generation has none
  const readyTabs = tabs.filter(hasReport);
  const viewOf = (tab) =>
    hasReport(tab) && hasFindings(tab) ? views[tab.id] || "report" : hasFindings(tab) ? "findings" : "report";
  const template = activeReportTemplate(reportContext);

  const modelList = models?.models || [];
  const [modelId, setModelId] = useState(models?.effective_default ?? null);
  const running = runningModelIds(generations);
  const atLimit = aiDraft.runningCount >= (aiDraft.maxConcurrent || 3);
  const modelName = (id) => modelList.find((m) => m.id === id)?.name || id;

  // the models arrive after the panel can open: preselect the user's default once they do
  useEffect(() => {
    if (modelId == null && models?.effective_default) setModelId(models.effective_default);
  }, [models?.effective_default]);

  // no tab open yet (just opened, or the open one was closed): pick one; never move away from an open tab
  useEffect(() => {
    if (current) return;
    const next = pickInitialGeneration(visibleTabs);
    if (next && next.id !== activeId) setActiveId(next.id);
  }, [current, visibleTabs.length]);

  // opening a finished draft's tab counts as viewing it
  useEffect(() => {
    if (current && isUnseen(current)) aiDraft.markViewed(current.id);
  }, [current?.id, current?.status]);

  // ticks the "Waiting for …" timer while a generation runs
  const anyPending = tabs.some(isPending);
  useEffect(() => {
    if (!anyPending) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [anyPending]);

  const hasActiveReportTab = () => {
    const ctx = latestReport.current;
    return (
      Array.isArray(ctx.patientReports) &&
      ctx.patientReports.length > 0 &&
      (ctx.activeTabKey != null || ctx.currentActiveTab != null)
    );
  };

  /**
   * True when the report text is exactly the template the tab was opened with (e.g. the default
   * template that pre-fills a new report). The picker's copy is checked first; the stored template
   * (both HTML variants) is fetched when that copy is missing or differs.
   */
  const holdsOnlyItsTemplate = async (reportHtml, reportTemplate) => {
    if (isUntouchedTemplate(reportHtml, reportTemplate.originalHtml)) return true;
    if (!reportTemplate.id || !studyParentId) return false;
    const response = await getAiDraftTemplateHtml(studyParentId, reportTemplate.id);
    const variants = response?.status === 200 ? response.data?.data?.html || [] : [];
    return variants.some((html) => isUntouchedTemplate(reportHtml, html));
  };

  const insert = async (tab) => {
    setWorking(true);
    try {
      if (!hasActiveReportTab()) {
        if (!reportMenu && setReportMenu) setReportMenu(true);
        if (!(await waitUntil(hasActiveReportTab))) {
          showToastv2(TOAST_TYPE.ERROR, "Open the report and choose a report template, then insert the AI draft");
          return;
        }
      }
      const ctx = latestReport.current;
      if (ctx.activeTab === ADDENDUM_TAB) {
        setNotice({ title: "Addendum open", message: ADDENDUM_MESSAGE });
        return;
      }
      const editor = ctx.editorRef?.current?.editor;
      if (editor?.edit && typeof editor.edit.isDisabled === "function" && editor.edit.isDisabled()) {
        showToastv2(TOAST_TYPE.ERROR, "The report is not editable");
        return;
      }
      const reportHtml = editor?.html?.get ? editor.html.get() : ctx.model;
      if (!isReportEmpty(reportHtml) && !(await holdsOnlyItsTemplate(reportHtml, activeReportTemplate(ctx)))) {
        setNotice({ title: "Report panel not empty", message: NOT_EMPTY_MESSAGE });
        return;
      }
      // through the report state: the editor is controlled by `model` and re-mounts on layout changes
      ctx.handleModelChange(tab.html, false);
      if (typeof ctx.markReportAutosaveDirty === "function") ctx.markReportAutosaveDirty();
      aiDraft.markSelected(tab.id);
      showToastv2(TOAST_TYPE.SUCCESS, "AI draft inserted: review and edit before signing");
      // other generations still running: stay open so they can be compared when they arrive
      if (aiDraft.runningCount === 0) onClose();
    } finally {
      setWorking(false);
    }
  };

  /**
   * The LLM report chat input of the active report tab, once the report panel shows it; else null.
   * Same conditions as the report panel's chat (agentic tab, report sub-tab, editable): a chat
   * editor stays registered after the chat is hidden, so its presence alone is not enough.
   */
  const chatEditor = () => {
    const chat = latestAgentic.current;
    const ctx = latestReport.current;
    if (!chat?.isAgenticTab || typeof chat.getChatInputRef !== "function" || ctx.activeTab !== 1) return null;
    if (ctx.disableEditReport) return null;
    return chat.getChatInputRef(ctx.activeTabKey);
  };

  /**
   * Puts this generation's findings into the LLM report chat, one paragraph per finding, under the
   * chat's "Create a @<report> report." line, for the radiologist to review and send. Nothing is
   * sent or generated here.
   */
  const sendToChat = async (tab) => {
    const lines = findingsLines(tab.findingsHtml);
    if (lines.length === 0) {
      showToastv2(TOAST_TYPE.ERROR, "This draft has no findings to send");
      return;
    }
    setWorking(true);
    try {
      // only a report panel opened just now needs time to show its chat
      const opening = !reportMenu && !!setReportMenu;
      if (opening) setReportMenu(true);
      if (!chatEditor() && !(opening && (await waitUntil(() => !!chatEditor())))) {
        setNotice({ title: "LLM chat not open", message: NO_CHAT_MESSAGE });
        return;
      }
      const editor = chatEditor();
      const chat = latestAgentic.current;
      const ctx = latestReport.current;
      // no @mention in the chat yet: name the report the way the chat's own auto-fill does
      const tabTitle = ctx.patientReports?.find((r) => r.key === ctx.activeTabKey)?.reportDetails?.reportTemplateTitle;
      const agent = (chat.agenticReportAgents || []).find((a) => a.label?.toLowerCase() === tabTitle?.toLowerCase());
      const fallback =
        chat.selectedReportMention ||
        (agent ? { label: agent.label, value: agent.value } : ctx.llmReportTitle ? { label: ctx.llmReportTitle } : null);
      const nodes = findingsChatNodes(editor.children, lines, fallback);
      fillChatEditor(editor, nodes);
      chat.setInputValue?.(JSON.stringify(nodes));
      const mention = mentionOf(nodes);
      if (mention) chat.setSelectedReportMention?.(mention);
      // the chat must not auto-fill over the findings afterwards
      chat.setHasAutoPopulated?.(true);
      aiDraft.markSelected(tab.id);
      showToastv2(TOAST_TYPE.SUCCESS, "Findings added to the LLM report chat: review them and press Send");
      if (aiDraft.runningCount === 0) onClose();
    } finally {
      setWorking(false);
    }
  };

  const copy = async (tab) => {
    const findings = viewOf(tab) === "findings";
    const html = findings ? tab.findingsHtml : tab.html;
    const plain = findings ? findingsLines(html).join("\n") : htmlToPlainText(html);
    const what = findings ? "Findings" : "AI draft";
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      showToastv2(TOAST_TYPE.SUCCESS, `${what} copied`);
    } catch {
      showToastv2(TOAST_TYPE.ERROR, `Could not copy the ${what.toLowerCase()}`);
    }
  };

  const chooseOutput = (value) => {
    setOutput(value);
    saveOutput(value);
  };

  /**
   * Starts a new generation with this model; its tab appears without taking focus from the open one.
   * as: the output to ask for (Retry repeats the failed generation's); else the picker's choice.
   */
  const generate = async (model, as = null) => {
    const wanted = enableLlmReport ? as || output : "report";
    const result = await aiDraft.generate(model, template.id, wanted);
    if (result.outcome === "ok") {
      if (!current && result.id) setActiveId(result.id);
    } else if (result.outcome === "noKeyImages") {
      showToastv2(TOAST_TYPE.ERROR, "Mark key images to generate an AI draft");
    } else {
      showToastv2(TOAST_TYPE.ERROR, result.message || "Could not request an AI draft");
    }
  };

  const closeTab = (id) => {
    setClosedIds((prev) => new Set(prev).add(id));
    if (id === activeId) {
      const index = visibleTabs.findIndex((t) => t.id === id);
      const neighbour = visibleTabs[index + 1] || visibleTabs[index - 1];
      setActiveId(neighbour ? neighbour.id : null);
    }
  };

  const startCompare = () => {
    const left = current && isReady(current) ? current : readyTabs[readyTabs.length - 2];
    const right = [...readyTabs].reverse().find((t) => t.id !== left?.id);
    setComparePair([left?.id ?? null, right?.id ?? null]);
    setCompare(true);
  };

  const placeholderCount = (tab) =>
    tab.highlighted.count > 0 ? `${tab.highlighted.count} placeholder${tab.highlighted.count === 1 ? "" : "s"} to complete` : "";

  const statusIcon = (tab) =>
    isPending(tab) ? (
      <Spinner size={SpinnerSize.xSmall} ariaLabel="Generating" className="ai-draft-tab-spinner" />
    ) : isReady(tab) ? (
      "✓"
    ) : (
      "✗"
    );

  const regenerateBlocked = running.has(modelId)
    ? `Already generating with ${modelName(modelId)}`
    : atLimit
      ? `${aiDraft.maxConcurrent || 3} AI drafts are already generating for this study`
      : null;

  const controls = (
    <div className="ai-draft-controls" data-testid="ai-draft-controls">
      {modelList.length > 1 ? (
        <Dropdown
          ariaLabel="AI draft model"
          label=""
          selectedKey={modelId}
          options={modelList.map((m) => ({ key: m.id, text: m.name }))}
          onChange={(_, option) => option && setModelId(String(option.key))}
          styles={{ root: { minWidth: 220 } }}
        />
      ) : (
        modelList.length === 1 && <span className="ai-draft-panel-meta">Model: {modelList[0].name}</span>
      )}
      {enableLlmReport && (
        <Dropdown
          ariaLabel="AI draft output"
          label=""
          selectedKey={output}
          options={AI_DRAFT_OUTPUTS}
          onChange={(_, option) => option && chooseOutput(String(option.key))}
          styles={{ root: { minWidth: 170 } }}
        />
      )}
      <span title={regenerateBlocked || undefined}>
        <PrimaryButton
          text={tabs.length === 0 ? "Generate" : "Regenerate"}
          onClick={() => generate(modelId)}
          disabled={aiDraft.busy || !!regenerateBlocked}
        />
      </span>
      {readyTabs.length >= 2 && (
        <DefaultButton
          text={compare ? "Back to tabs" : "Compare"}
          onClick={() => (compare ? setCompare(false) : startCompare())}
        />
      )}
      {closedIds.size > 0 && (
        <DefaultButton text={`Show ${closedIds.size} closed`} onClick={() => setClosedIds(new Set())} />
      )}
    </div>
  );

  const draftBody = (tab, { header = false, insertButton = false } = {}) => {
    const seconds = elapsedSeconds(tab, now);
    const basedOn = describeGeneration(tab);
    // Compare (insertButton) is about reports: always the report there
    const view = insertButton ? "report" : viewOf(tab);
    const showReport = hasReport(tab) && view === "report";
    const showFindings = hasFindings(tab) && view === "findings";
    return (
      <section className="ai-draft-column" data-testid="ai-draft-column" aria-label={tab.label}>
        <header className="ai-draft-column-header">
          {header && <span className="ai-draft-column-label">{tab.label}</span>}
          <span className="ai-draft-column-meta">
            {[
              tab.model_name && `Model: ${tab.model_name}`,
              tab.completed_at && `Generated: ${formatTime(tab.completed_at)}`,
              formatLatency(tab.latency_ms),
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {tab.selected && (
            <span className="ai-draft-inserted-chip">{tab.output === "findings" ? "Sent to chat" : "Inserted"}</span>
          )}
        </header>
        {hasReport(tab) && hasFindings(tab) && !insertButton && (
          <div className="ai-draft-view-toggle" role="group" aria-label="Report or findings">
            {[
              { key: "report", text: "Report" },
              { key: "findings", text: "Findings" },
            ].map((v) => (
              <button
                key={v.key}
                type="button"
                aria-pressed={view === v.key}
                className={`ai-draft-view-button${view === v.key ? " ai-draft-view-button--active" : ""}`}
                onClick={() => setViews((prev) => ({ ...prev, [tab.id]: v.key }))}
              >
                {v.text}
              </button>
            ))}
          </div>
        )}
        {tab.output === "both" && hasReport(tab) && !hasFindings(tab) && (
          <div className="ai-draft-panel-meta" data-testid="ai-draft-no-findings">
            The model did not return a separate findings list for this draft.
          </div>
        )}
        {basedOn && (
          <div className="ai-draft-panel-meta" data-testid="ai-draft-based-on">
            {basedOn}
          </div>
        )}
        {tab.stale && !isPending(tab) && (
          <div className="ai-draft-panel-callout ai-draft-panel-callout--warning" role="status">
            Key images changed since this draft was generated. Regenerate to include the current key images.
          </div>
        )}
        {isPending(tab) && (
          <div className="ai-draft-pending" data-testid="ai-draft-pending" role="status" aria-live="polite">
            <div className="ai-draft-pending-text">
              <span>
                Generating with {tab.model_name || "the AI model"}…
                {seconds != null && <span data-testid="ai-draft-pending-timer"> {seconds} s</span>}
              </span>
            </div>
            <ProgressSteps definitions={DRAFT_STEPS} steps={tab.steps} now={now} testId="ai-draft-steps" />
          </div>
        )}
        {isFailed(tab) && (
          <div className="ai-draft-panel-callout ai-draft-panel-callout--error" role="alert">
            {friendlyDraftError(tab.error) || "This model could not produce a draft."}
            {/* where the request stopped (no steps for failures the AI model itself reported late) */}
            {tab.steps?.some((s) => s.state === "failed") && (
              <ProgressSteps definitions={DRAFT_STEPS} steps={tab.steps} now={now} testId="ai-draft-steps" />
            )}
          </div>
        )}
        {showReport && placeholderCount(tab) && (
          <div className="ai-draft-panel-placeholders" data-testid="ai-draft-placeholder-count">
            {placeholderCount(tab)}
          </div>
        )}
        {showReport && (
          <div
            className="ai-draft-panel-report"
            data-testid="ai-draft-html"
            // sanitised with the report allowlist; the only markup added is <mark> on placeholders
            dangerouslySetInnerHTML={{ __html: tab.highlighted.html }}
          />
        )}
        {showFindings && (
          <>
            <div className="ai-draft-panel-meta">
              Findings for the LLM report: send them to the report chat, review, then press Send there.
            </div>
            <div
              className="ai-draft-panel-report ai-draft-findings"
              data-testid="ai-draft-findings"
              // sanitised with the findings allowlist (lists and emphasis only)
              dangerouslySetInnerHTML={{ __html: tab.findingsHtml }}
            />
          </>
        )}
        {showReport && insertButton && (
          <div className="ai-draft-column-actions">
            <PrimaryButton text="Insert this draft" onClick={() => insert(tab)} disabled={working} />
            <DefaultButton text="Copy" onClick={() => copy(tab)} />
          </div>
        )}
        {tab.notes && <div className="ai-draft-panel-notes">{tab.notes}</div>}
      </section>
    );
  };

  const compareColumn = (side) => {
    const tab = readyTabs.find((t) => t.id === comparePair[side]);
    return (
      <div className="ai-draft-compare-column" key={side}>
        <Dropdown
          ariaLabel={side === 0 ? "Left draft" : "Right draft"}
          selectedKey={comparePair[side]}
          options={readyTabs.map((t) => ({ key: t.id, text: t.label }))}
          onChange={(_, option) =>
            option && setComparePair((prev) => (side === 0 ? [String(option.key), prev[1]] : [prev[0], String(option.key)]))
          }
        />
        {tab && draftBody(tab, { header: true, insertButton: true })}
      </div>
    );
  };

  const tabStrip = visibleTabs.length > 0 && (
    <div className="ai-draft-tabs" role="tablist" data-testid="ai-draft-tabs">
      {visibleTabs.map((t) => (
        <div
          key={t.id}
          className={`ai-draft-tab${t.id === current?.id ? " ai-draft-tab--active" : ""}${
            isUnseen(t) && t.id !== current?.id ? " ai-draft-tab--new" : ""
          }`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={t.id === current?.id}
            className="ai-draft-tab-button"
            onClick={() => setActiveId(t.id)}
          >
            {t.label} {statusIcon(t)}
          </button>
          <button type="button" className="ai-draft-tab-close" aria-label={`Close ${t.label}`} onClick={() => closeTab(t.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );

  const body = (
    <div className="ai-draft-panel-body" data-testid="ai-draft-panel">
      {/* what the drafts read from the clinical notes and history documents */}
      {studyParentId && <HistorySummary studyId={studyParentId} />}
      {controls}
      {tabs.length === 0 && (
        <div className="ai-draft-panel-meta">Choose a model and generate an AI draft from the marked key images.</div>
      )}
      {compare ? (
        <div className="ai-draft-compare" data-testid="ai-draft-compare">
          {compareColumn(0)}
          {compareColumn(1)}
        </div>
      ) : (
        <>
          {tabStrip}
          {tabs.length > 0 && visibleTabs.length === 0 && (
            <div className="ai-draft-panel-meta">All tabs are closed. They come back when the study is opened again.</div>
          )}
          {current && draftBody(current)}
        </>
      )}
    </div>
  );

  const primary = compare || !current
    ? []
    : isFailed(current)
      ? [
          {
            title: "Retry",
            onclick: () => generate(current.model_id, current.output),
            disabled: aiDraft.busy || running.has(current.model_id) || atLimit,
          },
        ]
      : isReady(current)
        ? viewOf(current) === "findings"
          ? [{ title: "Send to LLM chat", onclick: () => sendToChat(current), disabled: working }]
          : [{ title: "Insert this draft", onclick: () => insert(current), disabled: working }]
        : [];
  const secondary = [
    { title: "Close", onclick: onClose },
    ...(!compare && isReady(current) ? [{ title: "Copy", onclick: () => copy(current) }] : []),
  ];

  return (
    <>
      <ModalMain
        showModal={true}
        scrollableBody={true}
        modalDetails={{
          header: (
            <ModalHeader
              updatedModal={true}
              showPatientDetails={false}
              heading="AI Draft Report: review required"
              handleCancel={onClose}
            />
          ),
          body,
          footer: <ModalFooter DefaultButtonList={secondary} PrimaryButtonList={primary} />,
          width: compare ? "1240px" : "860px",
          maxWidth: "95vw",
        }}
      />
      {notice && (
        <ConfirmModal
          showConfirmModal={true}
          title={notice.title}
          message={notice.message}
          confirmButtonText="OK"
          handleConfirm={() => setNotice(null)}
          handleCloseIcon={() => setNotice(null)}
        />
      )}
    </>
  );
};

export default AiDraftPanel;
